import { useState } from 'react';
import { useTheme, themeColors } from '../ThemeContext';
import { expos, Expo } from '../../../mocks/expoData';
import { useTranslation } from 'react-i18next';

interface ExpoSelectionScreenProps {
  onSelectExpo: (expo: Expo) => void;
  selectedExpoId: string | null;
}

const statusColorsDark: Record<string, string> = {
  Active: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
  Upcoming: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  Past: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
};
const statusColorsLight: Record<string, string> = {
  Active: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Upcoming: 'bg-amber-100 text-amber-700 border border-amber-200',
  Past: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const categoryIcons: Record<string, string> = {
  Technology: 'ri-cpu-line',
  Healthcare: 'ri-heart-pulse-line',
  Sustainability: 'ri-leaf-line',
  Finance: 'ri-bank-line',
};

const ExpoSelectionScreen = ({ onSelectExpo, selectedExpoId }: ExpoSelectionScreenProps) => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  const FILTERS = [
    { key: 'All', label: t('expoSelect.filter.all') },
    { key: 'Active', label: t('expoSelect.filter.active') },
    { key: 'Upcoming', label: t('expoSelect.filter.upcoming') },
    { key: 'Past', label: t('expoSelect.filter.past') },
  ];

  const filtered = expos.filter(e => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.city.toLowerCase().includes(search.toLowerCase()) ||
      e.category.toLowerCase().includes(search.toLowerCase());
    const matchFilter = activeFilter === 'All' || e.status === activeFilter;
    return matchSearch && matchFilter;
  });

  const getStatusLabel = (status: string) => {
    if (status === 'Active') return t('status.active');
    if (status === 'Upcoming') return t('status.upcoming');
    return t('status.past');
  };

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg}`}>
      <div className={`sticky top-0 z-20 ${c.navBg} backdrop-blur-xl border-b ${c.navBorder} px-5 pt-12 pb-4`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={`text-xl font-black ${c.text}`}>{t('expoSelect.title')}</h1>
            <p className={`text-xs ${c.textMuted} mt-0.5`}>
              <i className="ri-map-pin-line mr-1"></i>4 {t('expoSelect.subtitle.events')}
            </p>
          </div>
          <div className={`w-9 h-9 flex items-center justify-center rounded-full ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff]' : 'bg-[#0070E0]/10 text-[#0070E0]'}`}>
            <i className="ri-notification-3-line text-lg"></i>
          </div>
        </div>

        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl ${c.bgInput} border ${c.border}`}>
          <i className={`ri-search-line text-sm ${c.textMuted}`}></i>
          <input type="text" placeholder={t('expoSelect.search.placeholder')} value={search} onChange={e => setSearch(e.target.value)} className={`flex-1 bg-transparent text-sm outline-none ${c.text}`} />
          {search && <button onClick={() => setSearch('')} className={`${c.textMuted} cursor-pointer whitespace-nowrap`}><i className="ri-close-line"></i></button>}
        </div>

        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer border ${activeFilter === f.key ? isDark ? 'bg-[#00d4ff] text-[#08090F] border-[#00d4ff]' : 'bg-[#0070E0] text-white border-[#0070E0]' : `${c.bgCard} ${c.text} ${c.border}`}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {activeFilter === 'All' && (
        <div className="px-5 pt-5">
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>{t('expoSelect.featured')}</p>
          {expos.filter(e => e.status === 'Active').map(expo => (
            <button key={expo.id} onClick={() => onSelectExpo(expo)}
              className={`w-full text-left rounded-2xl overflow-hidden border transition-all duration-200 active:scale-[0.98] cursor-pointer ${selectedExpoId === expo.id ? isDark ? 'border-[#00d4ff]/60' : 'border-[#0070E0]/60' : c.border} ${c.glow} mb-5`}>
              <div className="relative h-40 overflow-hidden">
                <img src={expo.coverImage} alt={expo.name} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white animate-pulse">● {t('expoSelect.live')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDark ? 'bg-[#00d4ff]/20 text-[#00d4ff]' : 'bg-white/20 text-white'}`}>{expo.category}</span>
                  </div>
                  <h2 className="text-white font-black text-lg leading-tight">{expo.name}</h2>
                  <p className="text-gray-300 text-xs mt-0.5"><i className="ri-map-pin-line mr-1"></i>{expo.city}, {expo.country} • {expo.dateRange}</p>
                </div>
              </div>
              <div className={`px-4 py-3 flex items-center justify-between ${c.bgCard}`}>
                <div className="flex gap-4">
                  {[{ icon: 'ri-building-2-line', val: `${expo.booths}`, label: t('expoSelect.booths') }, { icon: 'ri-group-line', val: expo.attendees, label: t('expoSelect.attendees') }, { icon: 'ri-layout-grid-line', val: `${expo.halls}`, label: t('expoSelect.halls') }].map(stat => (
                    <div key={stat.label} className="flex items-center gap-1.5">
                      <i className={`${stat.icon} text-sm ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}></i>
                      <div>
                        <div className={`text-xs font-bold ${c.text}`}>{stat.val}</div>
                        <div className={`text-[10px] ${c.textMuted}`}>{stat.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`flex items-center gap-1 text-xs font-bold ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}>
                  {t('expoSelect.enter')} <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="px-5 pb-4">
        <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
          {activeFilter === 'All' ? t('expoSelect.allEvents') : FILTERS.find(f => f.key === activeFilter)?.label}
        </p>
        <div className="flex flex-col gap-3">
          {filtered.filter(e => activeFilter !== 'All' || e.status !== 'Active').map(expo => (
            <button key={expo.id} onClick={() => onSelectExpo(expo)}
              className={`w-full text-left flex gap-3 p-4 rounded-2xl border transition-all duration-200 active:scale-[0.98] cursor-pointer ${c.bgCard} ${selectedExpoId === expo.id ? isDark ? 'border-[#00d4ff]/50' : 'border-[#0070E0]/50' : c.border}`}>
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                <img src={expo.coverImage} alt={expo.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`text-sm font-bold ${c.text} leading-tight`}>{expo.name}</h3>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${isDark ? statusColorsDark[expo.status] : statusColorsLight[expo.status]}`}>{getStatusLabel(expo.status)}</span>
                </div>
                <p className={`text-xs ${c.textMuted} mt-1`}><i className="ri-map-pin-line mr-1"></i>{expo.city}, {expo.country}</p>
                <p className={`text-xs ${c.textMuted}`}><i className="ri-calendar-line mr-1"></i>{expo.dateRange}</p>
                <div className="flex items-center gap-2 mt-2">
                  <i className={`${categoryIcons[expo.category] || 'ri-flag-line'} text-xs ${isDark ? 'text-[#a78bfa]' : 'text-amber-500'}`}></i>
                  <span className={`text-[10px] font-semibold ${isDark ? 'text-[#a78bfa]' : 'text-amber-600'}`}>{expo.category}</span>
                  <span className={`text-[10px] ${c.textMuted}`}>• {expo.booths} {t('expoSelect.booths')}</span>
                </div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className={`text-center py-12 ${c.textMuted}`}>
              <i className="ri-search-line text-3xl mb-2 block"></i>
              <p className="text-sm">{t('expoSelect.noResults')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpoSelectionScreen;
